import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getAllUsersBasic, getAllUsersDetails, getUserPhoto, getPresenceByUserIds } from '../services/graphService';
import { getPhotoManifest, cachePhoto } from '../services/backendPhotoService';
import { getDeltekEmployees, matchDeltek } from '../services/deltekService';
import { getOpenAssetEmployees } from '../services/openAssetService';
import { matchByEmail } from '../services/emailMatch';
import { isExcludedUser, syncExcludedUsers } from '../services/excludedUsersService';
import { loadCorrections } from '../services/orgOverridesService';
import { syncProfiles } from '../services/profileService';
import { syncRole } from '../services/userRolesService';
import { syncBadges } from '../services/gamificationService';
import { syncCongrats } from '../services/congratsService';
import { syncDeptSettings, getDeptRenames } from '../services/deptSettingsService';
import { syncAppConfig } from './useAppConfig';
import { syncAwardMetalsConfig } from './useAwardMetalsConfig';
import { syncCareerPaths } from '../services/growth/rolesService';
import { useAuth } from './useAuth';

export const ERROR_NEEDS_CONSENT = 'NEEDS_CONSENT';

export function useEmployees() {
  const { getAccessToken, getApiToken, isAuthenticated, account } = useAuth();
  const [employees, setEmployees]     = useState([]);
  const [photos, setPhotos]           = useState({});
  const [presence, setPresence]       = useState({});
  const [openAssetData, setOpenAssetData] = useState(new Map());
  const [isLoading, setIsLoading]     = useState(false);
  // True once manager links + admin corrections have been applied. The org chart
  // waits for this so it renders the final hierarchy once, instead of flickering
  // through the pre-correction state (everyone a root) as data streams in.
  const [structureReady, setStructureReady] = useState(false);
  const hasInitializedRef             = useRef(false);
  const [error, setError]             = useState(null);

  const fetchEmployees = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setStructureReady(false);
    setError(null);

    try {
      const [token, apiToken] = await Promise.all([getAccessToken(), getApiToken()]);

      // Hydrate server-backed user state (profiles, role, exclusions, badges)
      // before the roster renders, so the exclusion filter below and admin
      // gates see server truth. Each sync is non-fatal and falls back to the
      // local cache, so a dead API never blocks the directory.
      await Promise.all([
        syncProfiles(apiToken),
        syncRole(apiToken),
        syncExcludedUsers(apiToken),
        syncBadges(apiToken, account?.localAccountId),
        syncCongrats(apiToken, account?.localAccountId),
        syncDeptSettings(apiToken),
        syncAppConfig(apiToken),
        syncAwardMetalsConfig(apiToken),
        syncCareerPaths(apiToken),
      ]);

      // ── Phase 1: basic Graph fields — fast, display immediately ───────────
      const basicUsers = await getAllUsersBasic(token);
      const filtered = basicUsers
        .filter((u) => {
          if (!u.userPrincipalName || !u.displayName) return false;
          if (u.userPrincipalName.includes('#EXT#')) return false;
          if (!u.mail) return false;
          if (!u.department && !u.jobTitle) return false;
          // Matches by id OR name rule — name rules survive AD re-syncs that
          // recreate an account under a new object id.
          if (isExcludedUser(u)) return false;
          return true;
        })
        .map((u) => ({ ...u, managerId: null }));

      setEmployees(filtered);
      setIsLoading(false);

      // ── Phase 2: enrich in background ─────────────────────────────────────
      const ids = filtered.map((u) => u.id);
      enrichBackground(token, apiToken, ids);

    } catch (err) {
      if (err.code === 'NEEDS_CONSENT' || err.message === 'NEEDS_CONSENT') {
        setError(ERROR_NEEDS_CONSENT);
      } else {
        console.error('Failed to fetch employees:', err);
        setError(err.message || 'Failed to load employee data');
      }
      setIsLoading(false);
    }
  }, [getAccessToken, getApiToken, isAuthenticated, account]);

  const enrichBackground = async (token, apiToken, ids) => {
    // Presence, Deltek, and OpenAsset all run in parallel alongside Graph enrichment
    getPresenceByUserIds(token, ids).then(setPresence).catch(() => {});

    // Deltek: enrich job title, department, office, phones
    getDeltekEmployees(apiToken).then((deltekMap) => {
      if (deltekMap.size === 0) return;
      setEmployees((prev) => prev.map((e) => {
        const d = matchDeltek(deltekMap, e.mail);
        if (!d) return e;
        return {
          ...e,
          jobTitle:       d.jobTitle   || e.jobTitle,
          mobilePhone:    d.mobilePhone || e.mobilePhone,
          businessPhones: d.businessPhone
            ? [d.businessPhone, ...(e.businessPhones ?? [])].slice(0, 1)
            : e.businessPhones,
          deltekId: d.deltekId,
          hireDate: d.hireDate || e.hireDate,
        };
      }));
    }).catch(() => {});

    // OpenAsset: cache the map so we can merge photos + extra fields
    getOpenAssetEmployees(apiToken).then((oaMap) => {
      if (oaMap.size === 0) return;
      setOpenAssetData(oaMap);
      setEmployees((prev) => prev.map((e) => {
        const oa = matchByEmail(oaMap, e.mail);
        if (!oa) return e;
        return {
          ...e,
          bio:    oa.bio    ?? e.bio    ?? null,
          skills: oa.skills ?? e.skills ?? [],
          openAssetProjects: oa.projects ?? [],
        };
      }));
    }).catch(() => {});

    // Photos don't affect org structure, so stream them independently — never let
    // them gate the hierarchy (waiting on hundreds of photos was what left the chart
    // showing the pre-correction structure for several seconds).
    loadPhotos(token, apiToken, ids);

    // Structure-critical data: Graph manager links + admin corrections. Fetch both,
    // then apply them in a SINGLE state update so the org chart transitions straight
    // from "loading" to the final hierarchy — no half-built intermediate render.
    try {
      const [detailsMap, corrections] = await Promise.all([
        getAllUsersDetails(token).catch(() => new Map()),
        loadCorrections(apiToken).catch(() => ({})),
      ]);

      setEmployees((prev) => prev.map((e) => {
        let next = e;

        const d = detailsMap.get(e.id);
        if (d) {
          // If a person's manager is excluded (e.g. left the company), drop the
          // managerId so the report becomes a root and the derived `manager` clears.
          const managerExcluded = d.manager && isExcludedUser(d.manager);
          next = {
            ...next,
            ...d,
            hireDate: next.hireDate || d.hireDate,
            managerId: managerExcluded ? null : (d.manager?.id || null),
          };
        }

        const c = corrections[e.id];
        if (c) {
          next = {
            ...next,
            ...(('managerId' in c) ? { managerId: c.managerId } : {}),
            ...(('department' in c && c.department !== null) ? { department: c.department } : {}),
            ...(('jobTitle' in c && c.jobTitle !== null) ? { jobTitle: c.jobTitle } : {}),
            ...(('topOfOrg' in c && c.topOfOrg !== null) ? { topOfOrg: Boolean(c.topOfOrg) } : {}),
          };
        }

        return next;
      }));
    } finally {
      setStructureReady(true);
    }
  };

  // Backend-first photo loading:
  // 1. Fetch manifest from Azure Blob Storage — sets all pre-cached photos instantly.
  // 2. Stream from Graph only for employees missing from the manifest.
  const loadPhotos = async (token, apiToken, ids) => {
    const manifest   = await getPhotoManifest().catch(() => ({}));
    const cachedIds  = new Set(Object.keys(manifest));

    if (cachedIds.size > 0) {
      setPhotos(manifest);
    }

    const missing = ids.filter((id) => !cachedIds.has(id));
    if (missing.length === 0) return;

    // Stream uncached photos from Graph, 10 in-flight at a time
    const CONCURRENCY = 10;
    const queue = [...missing];
    let active = 0;
    let completed = 0;

    await new Promise((resolve) => {
      const next = () => {
        while (active < CONCURRENCY && queue.length > 0) {
          const id = queue.shift();
          active++;
          getUserPhoto(token, id)
            .then((blob) => {
              if (blob) {
                setPhotos((prev) => ({ ...prev, [id]: URL.createObjectURL(blob) }));
                // Warm the shared cache with the signed-in user's OWN photo only.
                // The backend rejects caching anyone else's (anti-poisoning), so
                // don't bother firing those requests.
                if (id === account?.localAccountId) cachePhoto(apiToken, id, blob);
              }
            })
            .catch(() => {})
            .finally(() => {
              active--;
              completed++;
              if (completed === missing.length) resolve();
              else next();
            });
        }
      };
      next();
    });
  };

  useEffect(() => {
    if (isAuthenticated && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchEmployees();
    }
  }, [isAuthenticated, fetchEmployees]);

  // Bumped when local rules (dept renames/leads) change so dependent memos
  // re-apply them WITHOUT a full roster refetch — rules are render-time.
  const [localRulesVersion, setLocalRulesVersion] = useState(0);
  const applyLocalRules = useCallback(() => setLocalRulesVersion((v) => v + 1), []);

  // Admin department renames apply at roster-build time, so a rename (or a
  // merge of duplicate names like "IT" → "Information Technology") holds
  // everywhere — chips, cards, org chart — and survives AD re-syncs.
  const deptRenames = getDeptRenames();
  const renameDept = (d) => (d && deptRenames[d.trim().toLowerCase()]) || d;

  const departments = [...new Set(employees.map((e) => renameDept(e.department)).filter(Boolean))].sort();
  const offices     = [...new Set(employees.map((e) => e.officeLocation).filter(Boolean))].sort();

  // Photo priority: OpenAsset headshot > Graph API photo.
  // Manager is derived from the (possibly corrected) managerId against the current
  // roster — a single source of truth so "Reports to" corrections and force-excluded
  // managers flow through to the profile Org Hierarchy and the org chart alike.
  const employeesWithPresence = useMemo(() => {
    const byId = new Map(employees.map((e) => [e.id, e]));
    return employees.map((e) => {
      const oaPhoto = matchByEmail(openAssetData, e.mail)?.headshotUrl;
      return {
        ...e,
        department: renameDept(e.department),
        manager: e.managerId ? byId.get(e.managerId) ?? null : null,
        availability: presence[e.id] || null,
        openAssetPhoto: oaPhoto ?? null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, openAssetData, presence, localRulesVersion]);

  const buildOrgHierarchy = useCallback(() => {
    const map   = new Map(employees.map((e) => [e.id, { ...e, children: [] }]));
    const roots = [];
    employees.forEach((e) => {
      const node = map.get(e.id);
      if (e.managerId && map.has(e.managerId)) {
        map.get(e.managerId).children.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  }, [employees]);

  return {
    employees: employeesWithPresence,
    photos,
    departments,
    offices,
    isLoading,
    structureReady,
    error,
    refetch: fetchEmployees,
    applyLocalRules,
    buildOrgHierarchy,
  };
}
